import type { Node, NodeProps } from 'react-flow-renderer';
import type { Action } from 'flow/store/engine/types';

/** types of supported nodes  */
export const workflowNodeType = {
  exit: 'exit',
  folder: 'folder',
  importedTailNode: 'importedTailNode',
  inputAttribute: 'inputAttribute',
  matrixModel: 'matrixModel',
  outcome: 'outcome',
  outcomeReason: 'outcomeReason',
  outputAttribute: 'outputAttribute',
  preview: 'preview',
  riskCalculation: 'riskCalculation',
  snippet: 'snippet',
  service: 'service',
  skeleton: 'skeleton',
  tag: 'tag',
  workflow: 'workflow',
} as const;

export type WorkflowNodeType = keyof typeof workflowNodeType;

/** Generic type to represent the props each node component gets */
export interface WorkflowNodeProps<N extends WorkflowNode = WorkflowNode> extends NodeProps<N['data']> {
  type: N['type'];
}

export type WorkflowNode =
  | ExitNode
  | FolderNode
  | ImportedTailNode
  | InputAttributeNode
  | MatrixModelNode
  | OutcomeNode
  | OutcomeReasonNode
  | OutputAttributeNode
  | PreviewNode
  | RiskCalculationNode
  | SnippetNode
  | ServiceNode
  | SkeletonNode
  | TagNode
  | WorkflowRoutingNode;

export interface BaseWorkflowNode extends Node {
  type: WorkflowNodeType;
  data: BaseWorkflowNodeData;
}

export interface ExitNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['exit'];
  data: BaseWorkflowNodeData;
}

/**
 * When collapsed, a FolderNode's children are populated and do not
 * exist within a graph's nodes. When expanded, children is not populated
 * and exist within a graph's nodes.
 */
export interface FolderNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['folder'];
  data: BaseWorkflowNodeData & {
    childType: (typeof workflowNodeType)['inputAttribute'];
    children: InputAttributeNode[];
  };
}

export interface ImportedTailNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['importedTailNode'];
  data: BaseWorkflowNodeData;
}

export interface InputAttributeNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['inputAttribute'];
  data: BaseWorkflowNodeData & {
    isPublishedAttribute: boolean;
    objectType: 'entity' | 'transaction';
  };
}

export interface MatrixModelNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['matrixModel'];
  data: BaseWorkflowNodeData;
}

export interface OutcomeNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['outcome'];
  data: BaseWorkflowNodeData & {
    precedenceIndex: number;
  };
}

export interface OutcomeReasonNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['outcomeReason'];
  data: BaseWorkflowNodeData & {
    color: string;
  };
}

export interface OutputAttributeNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['outputAttribute'];
  data: BaseWorkflowNodeData & {
    isPublishedAttribute: boolean;
    objectType: 'entity' | 'transaction';
  };
}

export interface RiskCalculationNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['riskCalculation'];
  data: BaseWorkflowNodeData & {
    isPublishedAttribute: boolean;
  };
}

export interface PreviewNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['preview'];
  data: BaseWorkflowNodeData;
}

export interface ServiceNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['service'];
  data: BaseWorkflowNodeData;
}
export interface SkeletonNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['skeleton'];
  data: BaseWorkflowNodeData;
}

export interface TagNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['tag'];
  data: BaseWorkflowNodeData & {
    isCaseGenerating: boolean;
    hasOutcomes: boolean;
    color: string;
  };
}

export interface WorkflowRoutingNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['workflow'];
  data: BaseWorkflowNodeData & {
    precedenceIndex: number;
  };
}

export type SnippetChildNode =
  | MatrixModelNode
  | OutputAttributeNode
  | ServiceNode
  | TagNode;

export interface SnippetNode extends BaseWorkflowNode {
  type: (typeof workflowNodeType)['snippet'];
  data: BaseWorkflowNodeData & {
    children: SnippetChildNode[];
  };
}

export type ContainerNode = FolderNode | SnippetNode;

/** base data all nodes share */
interface BaseWorkflowNodeData {
  label: string;
  // types related to how the node should behave in the layout
  layout: {
    /**
     * if true, the node is fixed at its initial position as the user pans and scrolls around
     * if false, the node moves with the rest of the canvas as the user pans and scrolls around
     */
    fixed: boolean;
    /**
     * if true, the node is automatically positioned by the lay out algorithm
     * if false, the node must be manually positioned
     */
    autoLayout: boolean;
    /**
     * if true, the node is considered as an incomer in dependency bundle mappings
     * if false, the node is excluded from incomer relationships (this impacts dependency mappings)
     */
    asIncomer: boolean;
    /**
     * if true, the node is considered as an outgoer in dependency bundle mappings
     * if false, the node is excluded from outgoer relationships (this impacts dependency mappings)
     */
    asOutgoer: boolean;
    /**
     * if true, the node is floated up on isolation mode
     * if false, the node stays in the same row during isolation mode
     */
    float: boolean;
    /**
     * If populated, maps to the snippet id that contains this node.
     */
    containerId?: Action['publicId'] | null;
  };
}


/** states a node can be in */
export type NodeState =
  // default node state
  | 'normal'
  // when the cursor is in the node
  | 'hovered'
  // when the node is clicked & selected
  | 'focused'
  // when the node is focused and the cursor is in it
  | 'focused-hovered'
  // when the search query matches the node, but the search index is on another node
  | 'search-unfocused'
  // when the search query matches the node, and the search index is on this node
  | 'search-focused'
  // when the node is floated up the canvas
  | 'floated'
  // when there's other floated nodes but this one isn't
  // or, when the search query does not match the node
  | 'faded'
  // when there's a focused node and this node is an ancestry of it (but not a direct one)
  | 'deemphasized'
  // when the node needs to draw the user's attention via some external interaction
  | 'highlighted';
